// src/pages/PrivacyPage.js
//
// Privacy Policy. Layout chrome lives in <LegalPageShell> — this file
// is purely the policy text. Link colors come from the shell's prose
// styles (accent-aware), so inline color:'#60a5fa' has been removed.

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import LegalPageShell from '../components/LegalPageShell';

const PrivacyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Nexus';
  }, []);

  return (
    <LegalPageShell title="Privacy Policy" updated="July 15, 2026">
      <p className="intro-text">
        This Privacy Policy describes how Nexus ("we," "us," or "our") collects, uses, and protects your information when you use Nexus ("the Service").
      </p>

      <h2>1. Information We Collect</h2>

      <h3>Information You Provide</h3>
      <p>We collect information you provide directly to us, including:</p>
      <ul>
        <li><strong>Account information:</strong> Email address, password, name</li>
        <li><strong>Profile information:</strong> Business name, business details</li>
        <li><strong>Business data:</strong> Inventory records, sales data, expense records, buyer information, and other data you enter into the platform</li>
        <li><strong>Payment information:</strong> When you subscribe to paid features, payment is processed by our third-party payment processor (we do not store full credit card numbers)</li>
        <li><strong>Communications:</strong> Messages you send to us for support or feedback</li>
      </ul>

      <h3>Information Collected Automatically</h3>
      <p>When you use the Service, we automatically collect:</p>
      <ul>
        <li><strong>Device information:</strong> Browser type, operating system, device type</li>
        <li><strong>Usage information:</strong> Pages visited, features used, actions taken within the Service</li>
        <li><strong>Log data:</strong> IP address, access times, referring URLs</li>
        <li><strong>Cookies and similar technologies:</strong> Session cookies, preference cookies (see Section 8)</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Provide, maintain, and improve the Service</li>
        <li>Process transactions and manage your subscription</li>
        <li>Send technical notices, updates, security alerts, and support messages</li>
        <li>Respond to your comments, questions, and support requests</li>
        <li>Monitor and analyze trends, usage, and activities to improve user experience</li>
        <li>Detect, prevent, and address technical issues, fraud, or security concerns</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p>We do <strong>not</strong> use your information to:</p>
      <ul>
        <li>Sell your personal data to third parties</li>
        <li>Send unsolicited marketing from third parties</li>
        <li>Make automated decisions that significantly affect you without human review</li>
      </ul>

      <h2>3. How We Share Your Information</h2>
      <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>

      <h3>Service Providers</h3>
      <p>We share information with third-party service providers who perform services on our behalf:</p>
      <ul>
        <li><strong>Payment Processing:</strong> Stripe — to process subscription payments</li>
        <li><strong>Hosting & Infrastructure:</strong> To store and serve application data</li>
        <li><strong>Email:</strong> To send transactional emails</li>
      </ul>
      <p>These providers are contractually obligated to protect your information and may only use it to provide services to us.</p>

      <h3>Legal Requirements</h3>
      <p>We may disclose your information if required by law, court order, or government request, or if we believe disclosure is necessary to:</p>
      <ul>
        <li>Comply with legal obligations</li>
        <li>Protect our rights, property, or safety</li>
        <li>Protect the rights, property, or safety of our users or others</li>
        <li>Detect, prevent, or address fraud or security issues</li>
      </ul>

      <h3>Business Transfers</h3>
      <p>If we are involved in a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction. We will notify you of any such change.</p>

      <h2>4. Data Security</h2>
      <p>We implement reasonable security measures to protect your information, including:</p>
      <ul>
        <li>Encryption of data in transit (HTTPS/TLS)</li>
        <li>Encryption of sensitive data at rest</li>
        <li>Secure password hashing</li>
        <li>Regular security assessments</li>
        <li>Access controls limiting employee access to user data</li>
      </ul>
      <p>However, no method of transmission or storage is 100% secure. We cannot guarantee absolute security of your data.</p>

      <h3>Data Breach Notification</h3>
      <p>In the event of a data breach that affects your personal information, we will:</p>
      <ul>
        <li>Notify affected users within 72 hours of discovery via email</li>
        <li>Provide information about what data was affected</li>
        <li>Describe steps we are taking to address the breach</li>
        <li>Offer guidance on steps you can take to protect yourself</li>
      </ul>

      <h2>5. Data Retention</h2>
      <p>We retain your information for as long as:</p>
      <ul>
        <li>Your account is active</li>
        <li>Needed to provide you services</li>
        <li>Required to comply with legal obligations</li>
        <li>Necessary to resolve disputes or enforce agreements</li>
      </ul>
      <p>After account deletion, we may retain certain information for up to 90 days for backup and recovery purposes, and may retain anonymized/aggregated data indefinitely for analytics.</p>

      <h2>6. Your Rights and Choices</h2>
      <p>You have the right to:</p>
      <ul>
        <li><strong>Access</strong> your personal data stored in our systems</li>
        <li><strong>Correct</strong> inaccurate or incomplete data</li>
        <li><strong>Delete</strong> your account and associated data</li>
        <li><strong>Export</strong> your data in a portable format (CSV/JSON)</li>
        <li><strong>Opt out</strong> of marketing communications</li>
        <li><strong>Withdraw consent</strong> where processing is based on consent</li>
      </ul>
      <p>To exercise these rights, visit your account settings or contact us through our <Link to="/support">Support page</Link>.</p>
      <p>We will respond to requests within 30 days. We may need to verify your identity before processing certain requests.</p>

      <h2>7. Additional Rights for Specific Jurisdictions</h2>

      <h3>For California Residents (CCPA)</h3>
      <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act:</p>
      <ul>
        <li><strong>Right to Know:</strong> You can request details about the personal information we collect, use, and disclose</li>
        <li><strong>Right to Delete:</strong> You can request deletion of your personal information</li>
        <li><strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising your rights</li>
        <li><strong>Do Not Sell:</strong> We do not sell personal information. If this changes, we will provide an opt-out mechanism.</li>
      </ul>

      <h3>For European Economic Area (EEA) Residents (GDPR)</h3>
      <p>If you are in the EEA, we process your data under the following legal bases:</p>
      <ul>
        <li><strong>Contractual necessity:</strong> To provide the Service you signed up for</li>
        <li><strong>Legitimate interest:</strong> To improve our services and prevent fraud</li>
        <li><strong>Consent:</strong> Where you have given explicit consent (e.g., marketing emails)</li>
        <li><strong>Legal obligation:</strong> To comply with applicable laws</li>
      </ul>
      <p>You have additional rights under GDPR including right to data portability, right to restrict processing, right to object to processing, and right to lodge a complaint with a supervisory authority.</p>

      <h2>8. Cookies and Local Storage</h2>
      <p>Nexus does <strong>not</strong> use traditional browser cookies for tracking or analytics. Instead, we use browser local storage for essential functionality only:</p>

      <h3>What We Store Locally</h3>
      <ul>
        <li><strong>Authentication tokens:</strong> To keep you logged in securely (managed by Supabase)</li>
        <li><strong>Theme preferences:</strong> Your selected theme, accent color, and background settings</li>
        <li><strong>UI state:</strong> Dismissed banners, onboarding completion status, and recent searches</li>
      </ul>
      <p>All locally stored data is <strong>functional and necessary</strong> for the Service to operate. We do not use any analytics cookies, tracking pixels, or behavioral profiling tools.</p>

      <h3>Third-Party Services</h3>
      <ul>
        <li><strong>Stripe:</strong> Our payment processor may set its own cookies during checkout to process payments securely. See <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.</li>
        <li><strong>Sentry:</strong> We use Sentry for error monitoring in production to identify and fix bugs. Sentry collects error data and basic context (not browsing behavior). See <a href="https://sentry.io/privacy/" target="_blank" rel="noopener noreferrer">Sentry's Privacy Policy</a>.</li>
        <li><strong>Google Fonts:</strong> We load fonts from Google's CDN, which may log IP addresses per Google's standard practices.</li>
      </ul>

      <h3>Managing Local Storage</h3>
      <p>You can clear locally stored data through your browser settings (Clear Site Data). Note that this will log you out and reset your preferences.</p>
      <p>For more details, see our <Link to="/cookies">Cookie Policy</Link>.</p>

      <h2>9. Children's Privacy</h2>
      <p>The Service is not intended for users under 18 years of age. We do not knowingly collect personal information from children under 18.</p>
      <p>If we learn that we have collected information from a child under 18, we will delete that information promptly. If you believe we have collected information from a child, please contact us immediately through our <Link to="/support">Support page</Link>.</p>

      <h2>9a. Google User Data (Gmail Integration)</h2>
      <p>
        Nexus offers an optional email-scraping feature that connects to your Gmail account
        via Google OAuth using the read-only Gmail scope. This section describes exactly how
        Google user data is accessed, used, shared, protected, retained, and deleted.
      </p>
      <p><strong>What we access:</strong> With your explicit consent, Nexus reads email messages
        in your connected Gmail inbox for the sole purpose of detecting retailer order-related
        emails (order confirmations, shipping updates, delivery notifications, cancellations,
        and returns). Nexus has read-only access — it cannot send, modify, or delete your email.</p>
      <p><strong>How we use it:</strong> Order-related emails are parsed to extract purchase
        details (store, items, quantities, prices, order numbers, tracking numbers, and status)
        so the Service can display your orders, packages, and inventory in one dashboard. Emails
        that are not order-related are not stored. Google user data is never used for advertising,
        never sold, and never used to determine creditworthiness or for lending purposes.</p>
      <p><strong>How it is shared:</strong> We do not sell or transfer Google user data to third
        parties, with these narrow exceptions required to operate the feature: email content is
        processed by Anthropic's Claude API solely to extract order details (under Anthropic's
        commercial API terms, this data is not used to train their models), and extracted order
        data is stored in our database hosted by Supabase. Both act as service providers processing
        data only on our instructions.</p>
      <p><strong>AI/ML Limited Use:</strong> The use of raw or derived user data received from
        Workspace APIs adheres to the Google User Data Policy, including the Limited Use
        requirements. Google user data is not used to develop, improve, or train generalized
        AI or machine-learning models, and is not transferred to third-party services that use
        it to train their models.</p>
      <p><strong>How it is protected:</strong> All data is transmitted over TLS and encrypted at
        rest. OAuth tokens are stored encrypted server-side and are never exposed to your browser
        or other users. Database access is protected by row-level security so each user can only
        access their own data.</p>
      <p><strong>Retention and deletion:</strong> Extracted order data is retained while your
        account is active so your dashboard keeps working. You can disconnect Gmail at any time
        in the app (which stops all access and deletes stored OAuth tokens), revoke access from
        your Google Account security settings, delete individual scraped orders in the app, or
        delete your Nexus account — which permanently deletes your stored data. Raw email content
        is processed transiently and is not retained after parsing.</p>

      <h2>10. International Data Transfers</h2>
      <p>Your information may be transferred to and processed in the United States or other countries where our service providers operate. These countries may have different data protection laws than your country of residence.</p>
      <p>By using the Service, you consent to the transfer of your information to the United States. We take steps to ensure your data remains protected in accordance with this Privacy Policy.</p>

      <h2>11. Third-Party Links</h2>
      <p>The Service may contain links to third-party websites or services. We are not responsible for the privacy practices of these third parties. We encourage you to read their privacy policies before providing any information.</p>

      <h2>12. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time. We will notify you of changes by:</p>
      <ul>
        <li>Posting the new policy on this page</li>
        <li>Updating the "Last updated" date</li>
        <li>Sending an email notification for material changes</li>
      </ul>
      <p>Your continued use of the Service after changes constitutes acceptance of the updated policy.</p>

      <h2>13. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or our data practices, please contact us through our <Link to="/support">Support page</Link>.
      </p>
    </LegalPageShell>
  );
};

export default PrivacyPage;
